<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBSemi from '@/types/ferme/semi'
import type MDBAlerte from '@/types/alerte';
import { notifyError, toDate_wd_dd_MMMM, toDate_wd_dd_MMMM_hh_mm } from '@/utils/helpers'
import type MDBVegcoBucket from '@/types/vegco/bucket';



const theme = useTheme()
const storeU = useUtilisateurStore()
const appStore = useAppStore()
const router = useRouter()

const alertes = ref([] as MDBAlerte[])
const search = ref('')
const isLoading = ref(true)
const voirConfirmés = ref(false)

interface SévéritéInfo {
  label: string
  color: string
  icon: string
}

function getSévérité(sévérité: number): SévéritéInfo {
  switch (sévérité) {
    case 1: return { label: 'Info', color: 'info', icon: 'M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z' }
    case 2: return { label: 'Avertissement', color: 'warning', icon: 'M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16' }
    case 3: return { label: 'Erreur', color: 'error', icon: 'M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z' }
    case 4: return { label: 'Critique', color: 'error', icon: 'M8.27,3L3,8.27V15.73L8.27,21H15.73C17.5,19.24 21,15.73 21,15.73V8.27L15.73,3M9.1,5H14.9L19,9.1V14.9L14.9,19H9.1L5,14.9V9.1M11,15H13V17H11V15M11,7H13V13H11V7' }
    default: return { label: 'Debug', color: 'grey', icon: 'M20,8H17.19C16.74,7.2 16.12,6.5 15.37,6L17,4.41L15.59,3L13.42,5.17C12.96,5.06 12.5,5 12,5C11.5,5 11.05,5.06 10.59,5.17L8.41,3L7,4.41L8.62,6C7.87,6.5 7.26,7.21 6.81,8H4V10H6.09C6.03,10.33 6,10.66 6,11V12H4V14H6V15C6,15.34 6.03,15.67 6.09,16H4V18H6.81C8.47,20.87 12.14,21.84 15,20.18C15.91,19.66 16.67,18.9 17.19,18H20V16H17.91C17.97,15.67 18,15.34 18,15V14H20V12H18V11C18,10.66 17.97,10.33 17.91,10H20V8M16,15A4,4 0 0,1 12,19A4,4 0 0,1 8,15V11A4,4 0 0,1 12,7A4,4 0 0,1 16,11V15M14,10V12H10V10H14M10,14H14V16H10V14Z' }
  }
}

const MESSAGE_MAX_LENGTH = 180
const expandedMessages = ref(new Set<string>())

function isMessageLong(message: string): boolean {
  return (message?.length ?? 0) > MESSAGE_MAX_LENGTH
}

function getMessagePreview(message: string): string {
  return message.slice(0, MESSAGE_MAX_LENGTH).trimEnd() + '…'
}

function toggleMessage(id: string) {
  if (expandedMessages.value.has(id))
    expandedMessages.value.delete(id)
  else
    expandedMessages.value.add(id)
  expandedMessages.value = new Set(expandedMessages.value)
}
const headers: DataTableHeaders = [
  { title: 'Date', key: 'formattedCréeLe', width: '140px', sortable: false },
  { title: 'Sévérité', key: 'sévérité', width: '80px', sortable: false },
  { title: 'Type', key: 'typeAlerte', width: '180px', sortable: false },
  { title: 'Sujet', key: 'sujet', width: '110px', sortable: false },
  { title: 'Message', key: 'message', sortable: false },
  { title: 'Confirmé le', key: 'formattedConfirméLe', width: '130px', sortable: false },
  { title: 'Confirmé par', key: 'confirméPar', width: '140px', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
];
const formattedAlertes = computed(() => {
  let al = alertes.value.filter(alerte => alerte.destinataireType !== 'Agrimétrie')

  if (!voirConfirmés.value) {
    al = al.filter(alerte => {
      return new Date(alerte.confirméLe).getFullYear() <= 2000
    })
  }

  return al
    .slice()
    .sort((a, b) => new Date(b.crééLe).getTime() - new Date(a.crééLe).getTime())
    .map(alerte => ({
      ...alerte,
      formattedCréeLe: toDate_wd_dd_MMMM_hh_mm(alerte.crééLe),
      sujet: alerte.typeSujet,
      message: alerte.message || 'Aucun message',
      formattedConfirméLe: new Date(alerte.confirméLe).getFullYear() > 2000 ? toDate_wd_dd_MMMM(alerte.confirméLe) : 'Non confirmé',
    }));
})

function getSujet(alerte: MDBAlerte) {
  if (alerte.typeSujet === 'Semi' || alerte.typeSujet === 'Semi.Buckets') {
    return 'Semi'
  } else if (alerte.typeSujet === 'Vegco.Commande') {
    return 'Commande'
  } else if (alerte.typeSujet === 'Vegco.Bucket') {
    return 'Bucket'
  }
  return alerte.typeSujet;
}

async function openSujet(alerte: MDBAlerte) {
  try {
    if (alerte.typeSujet === 'Semi' || alerte.typeSujet === 'Semi.Buckets') {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const results = await apiProphet.get(`sites/${siteCS}/semis/${alerte.sujetId}`)
      const semi = results.data as MDBSemi

      if (alerte.typeSujet === 'Semi') {
        editSemi(semi)
      } else if (alerte.typeSujet === 'Semi.Buckets') {
        openBuckets(semi);
      }
    } else if (alerte.typeSujet === 'Vegco.Commande') {
      router.push('/vegco/commandes')
    } else if (alerte.typeSujet === 'Vegco.Bucket') {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const results = await apiProphet.get(`vgco/sites/${siteCS}/buckets/${alerte.sujetId}`)
      const bucket = results.data as MDBVegcoBucket
      openBucket(bucket);
    }
  }
  catch (error) {
    notifyError(error);
  }
}

async function confirmer(alerte: MDBAlerte, confirmer: boolean) {
  try {
    const config = { params: { confirmer: confirmer } }
    const result = await apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config)
    if (result.status == 200) {
      Notify.success('Alerte confirmée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error);
  }
}

const alertesNonConfirmées = computed(() =>
  formattedAlertes.value.filter(alerte => alerte.confirméPar == '')
)

const dialogToutConfirmer = ref(false)
const isConfirmingTout = ref(false)

async function confirmerTout() {
  try {
    isConfirmingTout.value = true
    const config = { params: { confirmer: true } }
    const àConfirmer = alertesNonConfirmées.value
    await Promise.all(
      àConfirmer.map(alerte =>
        apiProphet.put(`api/alertes/${alerte.id}/confirmer`, null, config)
      )
    )
    Notify.success(`${àConfirmer.length} alerte(s) confirmée(s) avec succès`)
    dialogToutConfirmer.value = false
    await getAlertes()
  } catch (error) {
    notifyError(error);
  } finally {
    isConfirmingTout.value = false
  }
}

async function effacer(alerte: MDBAlerte) {
  try {
    const result = await apiProphet.delete(`api/alertes/${alerte.id}`)
    if (result.status == 200) {
      Notify.success('Alerte effacée avec succès')
      getAlertes()
    }
  } catch (error) {
    notifyError(error);
  }
}

const dialogSemi = useTemplateRef('dialogSemi')
async function editSemi(semi: MDBSemi) {
  await dialogSemi.value?.openSemiEdit(semi)
}

const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets(semi: MDBSemi) {
  await dialogBuckets.value?.openBucketsAsync(semi, null)
}

const dialogBucket = useTemplateRef('dialogBucket')
async function openBucket(bucket: MDBVegcoBucket) {
  await dialogBucket.value?.openBucketEdit(bucket, undefined)
}

watch(() => storeU.siteSélectionné, async () => { await getAlertes() })

async function getAlertes() {
  try {
    isLoading.value = true;
    alertes.value = [];

    const results1 = await apiProphet.get('api/utilisateur/alertes')
    alertes.value = alertes.value.concat(results1.data as MDBAlerte[]);

    if (storeU.siteSélectionné.id) {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const results = await apiProphet.get(`sites/${siteCS}/alertes`)
      alertes.value = alertes.value.concat(results.data as MDBAlerte[]);
    }

    if (storeU.utilisateur.estAdministrateur) {
      const results2 = await apiProphet.get('api/alertes/systeme')
      alertes.value = alertes.value.concat(results2.data as MDBAlerte[]);
    }

    appStore.alertCount = alertes.value.filter(a =>
      a.destinataireType !== 'Agrimétrie' &&
      new Date(a.confirméLe).getFullYear() <= 2000
    ).length
  }
  catch (error) {
    notifyError(error);
  }
  finally {
    isLoading.value = false;
  }
}

const dialogNuméro = ref(false)
const numéroTéléphone = ref(storeU.utilisateur.paramètresAlertes.numéroTéléphone || '')
const numéroTéléphoneE164 = computed(() => {
  // Remove all non-digit characters and prepend +1
  const digits = numéroTéléphone.value.replace(/\D/g, '');
  return digits.length ? `+1${digits}` : '';
})
const heureDe = ref(storeU.utilisateur.paramètresAlertes.heureDe || 0)
const heureÀ = ref(storeU.utilisateur.paramètresAlertes.heureÀ || 24)

function openTéléphone() {
  dialogNuméro.value = true
  numéroTéléphone.value = storeU.utilisateur.paramètresAlertes.numéroTéléphone || ''
}

async function confirmTéléphone() {
  try
  {
    storeU.utilisateur.paramètresAlertes.numéroTéléphone = numéroTéléphone.value
    storeU.utilisateur.paramètresAlertes.numéroTéléphoneE164 = numéroTéléphoneE164.value
    storeU.utilisateur.paramètresAlertes.heureDe = heureDe.value
    storeU.utilisateur.paramètresAlertes.heureÀ = heureÀ.value
    await storeU.updateParamètresAlertes()
    dialogNuméro.value = false
  }
  catch (error) {
    notifyError(error);
  }
}

function cancelTéléphone() {
  dialogNuméro.value = false
}

onMounted(() => {
  getAlertes();
});
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M8.27,3L3,8.27V15.73L8.27,21H15.73C17.5,19.24 21,15.73 21,15.73V8.27L15.73,3M9.1,5H14.9L19,9.1V14.9L14.9,19H9.1L5,14.9V9.1M11,15H13V17H11V15M11,7H13V13H11V7" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Alertes</div>
            <div class="text-caption text-medium-emphasis">Notifications et messages reçus</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn
            v-if="alertesNonConfirmées.length > 0"
            color="success" variant="tonal" rounded="pill"
            prepend-icon="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z"
            @click="dialogToutConfirmer = true">
            Tout confirmer
          </v-btn>
          <v-btn
            color="primary" variant="tonal" rounded="pill"
            prepend-icon="M11,17V7H4V17H11M11,3A2,2 0 0,1 13,5V19A2,2 0 0,1 11,21H4C2.89,21 2,20.1 2,19V5A2,2 0 0,1 4,3H11M16.5,3H21.5A1.5,1.5 0 0,1 23,4.5V7.5A1.5,1.5 0 0,1 21.5,9H18L15,12V9L15,4.5A1.5,1.5 0 0,1 16.5,3Z"
            @click="openTéléphone()">
            SMS
          </v-btn>
        </div>
      </div>

      <!-- Secondary row: search + filter -->
      <div class="d-flex align-center flex-wrap mt-3" style="gap: 12px;">
        <v-text-field
          v-model="search"
          label="Rechercher"
          prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
          density="compact" variant="outlined" rounded="lg" hide-details clearable
          style="max-width: 320px; min-width: 180px;"/>
        <v-spacer/>
        <v-switch
          v-model="voirConfirmés"
          label="Voir confirmés"
          color="primary"
          class="flex-grow-0" hide-details density="compact"/>
      </div>
    </div>
    <v-divider/>

    <v-data-table
      :headers="headers"
      :items="formattedAlertes"
      :search="search"
      :loading="isLoading"
      item-value="id"
      :hide-default-header="$vuetify.display.smAndDown"
      no-data-text="Aucune alerte trouvée">

      <!-- Severity column -->
      <template #item.sévérité="{ item }">
        <v-chip
          :color="getSévérité(item.sévérité).color"
          :prepend-icon="getSévérité(item.sévérité).icon"
          size="small" variant="tonal" label>
          {{ getSévérité(item.sévérité).label }}
        </v-chip>
      </template>

      <!-- Message column with truncation -->
      <template #item.message="{ item }">
        <div class="message-cell py-2">
          <div class="message-text" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
          <v-btn
            v-if="isMessageLong(item.message)"
            size="x-small" color="primary" variant="text" class="mt-1 px-1"
            :append-icon="expandedMessages.has(item.id) ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'"
            @click="toggleMessage(item.id)">
            {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
          </v-btn>
        </div>
      </template>

      <!-- Confirmé status column -->
      <template #item.formattedConfirméLe="{ item }">
        <v-chip
          v-if="item.confirméPar == ''"
          color="grey" size="small" variant="tonal" label
          prepend-icon="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z">
          Non confirmé
        </v-chip>
        <v-chip
          v-else
          color="success" size="small" variant="tonal" label
          prepend-icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z">
          {{ item.formattedConfirméLe }}
        </v-chip>
      </template>

      <template #item.sujet="{ item }">
        <v-btn
          v-if="item.typeSujet !== ''"
          size="small" color="primary" variant="tonal" rounded="lg"
          @click="openSujet(item)">
          {{ getSujet(item) }}
        </v-btn>
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex align-center" style="gap: 4px;">
          <v-tooltip :text="item.confirméPar == '' ? 'Confirmer' : 'Enlever la confirmation'" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                :icon="item.confirméPar == '' ? 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' : 'M13.5,7A6.5,6.5 0 0,1 20,13.5A6.5,6.5 0 0,1 13.5,20H10V18H13.5C16,18 18,16 18,13.5C18,11 16,9 13.5,9H7.83L10.91,12.09L9.5,13.5L4,8L9.5,2.5L10.92,3.91L7.83,7H13.5M6,18H8V20H6V18Z'"
                :color="item.confirméPar == '' ? 'success' : 'warning'"
                size="small" variant="text"
                @click="confirmer(item, item.confirméPar == '')"/>
            </template>
          </v-tooltip>
          <v-tooltip v-if="storeU.utilisateur.estAdministrateur" text="Effacer" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8,9H16V19H8V9M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z" color="error"
                size="small" variant="text"
                @click="effacer(item)"/>
            </template>
          </v-tooltip>
        </div>
      </template>

      <!-- Mobile card layout -->
      <template v-if="$vuetify.display.smAndDown" #item="{ item }">
        <tr>
          <td class="pa-0">
            <v-card variant="flat" class="alerte-mobile-card pa-3 ma-2" rounded="lg">
              <div class="d-flex align-center justify-space-between mb-2" style="gap: 8px;">
                <v-chip
                  :color="getSévérité(item.sévérité).color"
                  :prepend-icon="getSévérité(item.sévérité).icon"
                  size="small" variant="tonal" label>
                  {{ getSévérité(item.sévérité).label }}
                </v-chip>
                <v-chip
                  :color="item.confirméPar == '' ? 'grey' : 'success'"
                  :prepend-icon="item.confirméPar == '' ? 'M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z' : 'M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z'"
                  size="x-small" variant="tonal" label>
                  {{ item.confirméPar == '' ? 'Non confirmé' : 'Confirmé' }}
                </v-chip>
              </div>

              <div class="text-body-2 font-weight-bold">{{ item.typeAlerte }}</div>
              <div class="text-caption text-medium-emphasis mb-2">{{ item.formattedCréeLe }}</div>

              <div class="text-body-2 mb-1" style="white-space: pre-wrap; word-break: break-word;">{{ isMessageLong(item.message) && !expandedMessages.has(item.id) ? getMessagePreview(item.message) : item.message }}</div>
              <v-btn
                v-if="isMessageLong(item.message)"
                size="x-small" color="primary" variant="text" class="mb-2 px-1"
                :append-icon="expandedMessages.has(item.id) ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'"
                @click="toggleMessage(item.id)">
                {{ expandedMessages.has(item.id) ? 'Voir moins' : 'Voir plus' }}
              </v-btn>

              <div class="d-flex flex-column text-caption text-medium-emphasis mb-2" style="gap: 2px;">
                <span><v-icon icon="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,6A2,2 0 0,0 10,8A2,2 0 0,0 12,10A2,2 0 0,0 14,8A2,2 0 0,0 12,6M12,13C14.67,13 20,14.33 20,17V20H4V17C4,14.33 9.33,13 12,13M12,14.9C9.03,14.9 5.9,16.36 5.9,17V18.1H18.1V17C18.1,16.36 14.97,14.9 12,14.9Z" size="14" class="me-1"/>Créé par : {{ item.crééPar }}</span>
              </div>

              <v-divider class="my-2"/>

              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-btn
                  v-if="item.typeSujet !== ''"
                  size="small" color="primary" variant="tonal" rounded="lg"
                  prepend-icon="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"
                  @click="openSujet(item)">
                  {{ getSujet(item) }}
                </v-btn>
                <v-spacer/>
                <v-btn
                  :prepend-icon="item.confirméPar == '' ? 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' : 'M13.5,7A6.5,6.5 0 0,1 20,13.5A6.5,6.5 0 0,1 13.5,20H10V18H13.5C16,18 18,16 18,13.5C18,11 16,9 13.5,9H7.83L10.91,12.09L9.5,13.5L4,8L9.5,2.5L10.92,3.91L7.83,7H13.5M6,18H8V20H6V18Z'"
                  :color="item.confirméPar == '' ? 'success' : 'warning'"
                  size="small" variant="text"
                  @click="confirmer(item, item.confirméPar == '')">
                  {{ item.confirméPar == '' ? 'Confirmer' : 'Annuler' }}
                </v-btn>
                <v-btn
                  v-if="storeU.utilisateur.estAdministrateur"
                  icon="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8,9H16V19H8V9M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z" color="error"
                  size="small" variant="text"
                  @click="effacer(item)"/>
              </div>
            </v-card>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>

  <DialogSemi ref='dialogSemi'/>
  <DialogBuckets ref="dialogBuckets"/>
  <DialogBucket ref="dialogBucket"/>

  <v-dialog v-model="dialogNuméro" max-width="600px" :fullscreen="$vuetify.display.xs">
    <v-card rounded="xl">
      <div class="dialog-header pa-4 d-flex align-center" style="gap: 12px;">
        <v-avatar color="white" variant="tonal" size="40" rounded="lg">
          <v-icon icon="M11,17V7H4V17H11M11,3A2,2 0 0,1 13,5V19A2,2 0 0,1 11,21H4C2.89,21 2,20.1 2,19V5A2,2 0 0,1 4,3H11M16.5,3H21.5A1.5,1.5 0 0,1 23,4.5V7.5A1.5,1.5 0 0,1 21.5,9H18L15,12V9L15,4.5A1.5,1.5 0 0,1 16.5,3Z" size="22"/>
        </v-avatar>
        <div>
          <div class="text-h6 font-weight-bold">Alertes par SMS</div>
          <div class="text-caption" style="opacity: 0.85;">Configurez votre numéro et vos heures de réception</div>
        </div>
      </div>
      <v-card-text class="pt-4">
        <v-container class="pa-0">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="numéroTéléphone"
                type="tel"
                variant="outlined" rounded="lg"
                prepend-inner-icon="M20,15.5C18.8,15.5 17.5,15.3 16.4,14.9C16.3,14.9 16.2,14.9 16.1,14.9C15.8,14.9 15.6,15 15.4,15.2L13.2,17.4C10.4,15.9 8,13.6 6.6,10.8L8.8,8.6C9.1,8.3 9.2,7.9 9,7.6C8.7,6.5 8.5,5.2 8.5,4C8.5,3.5 8,3 7.5,3H4C3.5,3 3,3.5 3,4C3,13.4 10.6,21 20,21C20.5,21 21,20.5 21,20V16.5C21,16 20.5,15.5 20,15.5M5,5H6.5C6.6,5.9 6.8,6.8 7,7.6L5.8,8.8C5.4,7.6 5.1,6.3 5,5M19,19C17.7,18.9 16.4,18.6 15.2,18.2L16.4,17C17.2,17.2 18.1,17.4 19,17.4V19Z"
                placeholder="123-456-7890"
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                :hint="`Format E.164 : ${numéroTéléphoneE164 || '—'}`" persistent-hint
                label="Numéro de téléphone"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="heureDe"
                type="number"
                variant="outlined" rounded="lg"
                prepend-inner-icon="M12,1C8.14,1 5,4.14 5,8A7,7 0 0,0 12,15C15.86,15 19,11.87 19,8C19,4.14 15.86,1 12,1M12,3.15C14.67,3.15 16.85,5.32 16.85,8C16.85,10.68 14.67,12.85 12,12.85A4.85,4.85 0 0,1 7.15,8A4.85,4.85 0 0,1 12,3.15M11,5V8.69L14.19,10.53L14.94,9.23L12.5,7.82V5M4,16V24H6V21H18V24L22,20L18,16V19H6V16"
                min="0" max="24"
                label="Heure minimale"
                placeholder="0-23"/>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="heureÀ"
                type="number"
                variant="outlined" rounded="lg"
                prepend-inner-icon="M12,1C8.14,1 5,4.14 5,8A7,7 0 0,0 12,15C15.86,15 19,11.87 19,8C19,4.14 15.86,1 12,1M12,3.15C14.67,3.15 16.85,5.32 16.85,8C16.85,10.68 14.67,12.85 12,12.85A4.85,4.85 0 0,1 7.15,8A4.85,4.85 0 0,1 12,3.15M11,5V8.69L14.19,10.53L14.94,9.23L12.5,7.82V5M15,16V19H3V21H15V24L19,20M19,20V24H21V16H19"
                min="0" max="24"
                label="Heure maximale"
                placeholder="0-23"/>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider/>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="cancelTéléphone">Annuler</v-btn>
        <v-btn color="primary" variant="flat" rounded="pill" @click="confirmTéléphone">Confirmer</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="dialogToutConfirmer" max-width="460px" :fullscreen="$vuetify.display.xs">
    <v-card rounded="xl">
      <div class="dialog-header pa-4 d-flex align-center" style="gap: 12px;">
        <v-avatar color="white" variant="tonal" size="40" rounded="lg">
          <v-icon icon="M0.41,13.41L6,19L7.41,17.58L1.83,12M22.24,5.58L11.66,16.17L7.5,12L6.07,13.41L11.66,19L23.66,7M18,7L16.59,5.58L10.24,11.93L11.66,13.34L18,7Z" size="22"/>
        </v-avatar>
        <div>
          <div class="text-h6 font-weight-bold">Tout confirmer</div>
          <div class="text-caption" style="opacity: 0.85;">Confirmer toutes les alertes affichées</div>
        </div>
      </div>
      <v-card-text class="pt-4">
        Voulez-vous vraiment confirmer
        <strong>{{ alertesNonConfirmées.length }}</strong>
        alerte(s) non confirmée(s) ? Cette action ne peut pas être annulée en masse.
      </v-card-text>
      <v-divider/>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" :disabled="isConfirmingTout" @click="dialogToutConfirmer = false">Annuler</v-btn>
        <v-btn
          color="success" variant="flat" rounded="pill"
          :loading="isConfirmingTout"
          @click="confirmerTout">
          Tout confirmer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Message cell ────────────────────────── */
.message-cell {
  max-width: 480px;
}
.message-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* ── Mobile alert card ───────────────────── */
.alerte-mobile-card {
  border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"');
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.18)" : "rgba(var(--v-theme-primary), 0.02)"');
}

/* ── Dialog header ───────────────────────── */
.dialog-header {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}
</style>
